<template>
  <div class="attribute-batch-input">
    <el-select v-if="definition.editable === false" :value="keepValue" disabled>
      <el-option :value="keepValue" label="不修改" />
    </el-select>
    <template v-else-if="['select', 'multi_select'].includes(definition.type)">
      <el-select
        :value="selectionValue"
        :multiple="definition.type === 'multi_select'"
        :disabled="disabled"
        filterable
        :allow-create="canCreate"
        :default-first-option="canCreate"
        placeholder="请选择属性值"
        @input="changeSelection">
        <el-option :value="keepValue" label="不修改" />
        <el-option v-if="canClear" :value="clearValue" label="清空属性内容" />
        <el-option
          v-for="option in definition.options"
          :key="option.value"
          :label="option.name"
          :value="option.value" />
      </el-select>
    </template>
    <template v-else-if="isMeasure">
      <el-select
        v-if="canDirectMeasure"
        :value="directMeasureValue"
        :disabled="disabled"
        filterable
        placeholder="请选择属性值"
        @input="changeDirectMeasure">
        <el-option :value="keepValue" label="不修改" />
        <el-option v-if="canClear" :value="clearValue" label="清空属性内容" />
        <el-option
          v-for="(option, optionIndex) in directMeasureOptions"
          :key="optionIndex"
          :value="enumValue(option)"
          :label="enumValue(option)" />
      </el-select>
      <el-select
        v-else-if="!enabled || value === clearValue"
        :value="enabled ? clearValue : keepValue"
        :disabled="disabled"
        @input="startMeasure">
        <el-option :value="keepValue" label="不修改" />
        <el-option v-if="canClear" :value="clearValue" label="清空属性内容" />
        <el-option
          v-for="template in definition.measure_templates"
          :key="String(template.template_id)"
          :label="template.display_name || template.template_name || template.name || String(template.template_id)"
          :value="template.template_id" />
      </el-select>
      <div v-for="(row, index) in enabled && value !== clearValue ? rows : []" :key="index" class="measure-row">
        <el-select
          :value="row.measure_info.template_id"
          :disabled="disabled"
          placeholder="选择度量衡模板"
          @change="changeTemplate(index, $event)">
          <el-option v-if="index === 0" :value="keepValue" label="不修改" />
          <el-option v-if="index === 0 && canClear" :value="clearValue" label="清空属性内容" />
          <el-option
            v-for="template in definition.measure_templates"
            :key="String(template.template_id)"
            :label="template.display_name || template.template_name || template.name || String(template.template_id)"
            :value="template.template_id" />
        </el-select>
        <div
          v-for="(module, moduleIndex) in modules(row)"
          v-if="!canDirectMeasure || index > 0 || moduleIndex > 0"
          :key="String(module.module_id)"
          class="measure-module">
          <span>{{ module.prefix }}</span>
          <el-select
            v-if="module.input_type === 'enum_diy'"
            :value="row.measure_info.values[moduleIndex].value"
            :disabled="disabled"
            placeholder="请选择"
            @input="changeValue(index, moduleIndex, 'value', String($event))">
            <el-option
              v-for="(option, optionIndex) in module.values || []"
              :key="optionIndex"
              :value="enumValue(option)"
              :label="enumValue(option)" />
          </el-select>
          <el-input
            v-else
            :value="row.measure_info.values[moduleIndex].value"
            :disabled="disabled"
            placeholder="请输入数值"
            @input="changeValue(index, moduleIndex, 'value', $event)" />
          <el-select
            v-if="(module.units || []).length"
            :value="row.measure_info.values[moduleIndex].unit_id"
            :disabled="disabled"
            placeholder="单位"
            @input="changeValue(index, moduleIndex, 'unit_id', $event)">
            <el-option
              v-for="unit in module.units"
              :key="String(unit.unit_id)"
              :value="unit.unit_id"
              :label="unit.unit_name" />
          </el-select>
          <span>{{ module.suffix }}</span>
        </div>
        <el-button v-if="rows.length > 1" type="text" :disabled="disabled" @click="removeRow(index)">删除</el-button>
      </div>
      <el-button
        v-if="enabled && value !== clearValue && (!rows.length || definition.type === 'multi_value_measure')"
        :disabled="disabled || rows.length >= 100"
        size="mini"
        @click="addRow">
        添加一组
      </el-button>
    </template>
    <el-input
      v-else
      :value="enabled ? value : keepValue"
      :disabled="disabled"
      :maxlength="1000"
      placeholder="不修改"
      @input="changeText" />
  </div>
</template>

<script>
export default {
  name: 'AttributeBatchInput',
  props: { definition: { type: Object, required: true }, value: { default: '' }, enabled: Boolean, disabled: Boolean },
  data: () => ({ keepValue: '不修改', clearValue: '清空属性内容' }),
  computed: {
    selectionValue() {
      const value = this.enabled ? this.value : this.keepValue
      return this.definition.type === 'multi_select' && typeof value === 'string' ? [value] : value
    },
    canClear() {
      return (
        !this.definition.required &&
        this.definition.editable !== false &&
        String(this.definition.property_id) !== '1687'
      )
    },
    isMeasure() {
      return ['measure', 'multi_value_measure'].includes(this.definition.type)
    },
    rows() {
      return Array.isArray(this.value) ? this.value : []
    },
    singleMeasureTemplate() {
      const templates = this.definition.measure_templates || []
      return templates.length === 1 ? templates[0] : null
    },
    directMeasureOptions() {
      return this.singleMeasureTemplate?.value_modules?.[0]?.values || []
    },
    canDirectMeasure() {
      return (
        this.singleMeasureTemplate?.value_modules?.[0]?.input_type === 'enum_diy' &&
        this.directMeasureOptions.length > 0
      )
    },
    directMeasureValue() {
      return !this.enabled
        ? this.keepValue
        : this.value === this.clearValue
        ? this.clearValue
        : this.rows[0]?.measure_info?.values?.[0]?.value || this.keepValue
    },
    canCreate() {
      return (
        String(this.definition.property_id) !== '1687' &&
        [true, 1, '1', 'true'].includes(this.definition.diy_type || this.definition.support_diy_type)
      )
    }
  },
  methods: {
    setValue(value, enabled = true) {
      this.$emit('enabled-change', enabled)
      this.$emit('input', value)
    },
    keepOriginal() {
      this.setValue(this.isMeasure || this.definition.type === 'multi_select' ? [] : '', false)
    },
    changeText(value) {
      if (value.trim() === this.keepValue) this.keepOriginal()
      else this.setValue(value.trim() === this.clearValue ? this.clearValue : value)
    },
    changeSelection(value) {
      if (this.definition.type === 'multi_select') {
        if (this.enabled && value.includes(this.keepValue)) return this.keepOriginal()
        value = value.filter((item) => item !== this.keepValue)
        if (value.includes(this.clearValue))
          value =
            this.value === this.clearValue && value.length > 1
              ? value.filter((item) => item !== this.clearValue)
              : this.clearValue
      } else if (value === this.keepValue) return this.keepOriginal()
      this.setValue(value)
    },
    startMeasure(id) {
      if (id === this.keepValue) return this.keepOriginal()
      if (id === this.clearValue) return this.setValue(this.clearValue)
      const template = (this.definition.measure_templates || []).find((item) => String(item.template_id) === String(id))
      if (template) this.setValue([this.makeRow(template)])
    },
    changeDirectMeasure(value) {
      if (value === this.keepValue) return this.keepOriginal()
      if (value === this.clearValue) return this.setValue(this.clearValue)
      const row = this.makeRow(this.singleMeasureTemplate)
      row.measure_info.values[0].value = String(value)
      this.setValue([row])
    },
    enumValue(option) {
      return String(
        typeof option === 'object'
          ? option.value !== undefined
            ? option.value
            : option.value_name !== undefined
            ? option.value_name
            : option.name || ''
          : option
      )
    },
    modules(row) {
      const template = (this.definition.measure_templates || []).find(
        (item) => String(item.template_id) === String(row.measure_info.template_id)
      )
      return template ? template.value_modules || [] : []
    },
    makeRow(template) {
      return {
        measure_info: {
          template_id: template.template_id,
          values: (template.value_modules || []).map((module) => ({
            module_id: module.module_id,
            value: '',
            unit_id: ((module.units || [])[0] || {}).unit_id || 0
          }))
        }
      }
    },
    addRow() {
      const template = (this.definition.measure_templates || [])[0]
      if (!template) return this.$message.warning('本地度量衡模板缺失，请先同步类目数据')
      this.$emit('input', [...this.rows, this.makeRow(template)])
    },
    removeRow(index) {
      this.$emit(
        'input',
        this.rows.filter((row, i) => i !== index)
      )
    },
    changeTemplate(index, id) {
      if (id === this.keepValue) return this.keepOriginal()
      if (id === this.clearValue) return this.setValue(this.clearValue)
      const template = (this.definition.measure_templates || []).find((item) => String(item.template_id) === String(id))
      if (!template) return
      const rows = this.rows.slice()
      rows.splice(index, 1, this.makeRow(template))
      this.$emit('input', rows)
    },
    changeValue(index, moduleIndex, key, value) {
      const rows = JSON.parse(JSON.stringify(this.rows))
      rows[index].measure_info.values[moduleIndex][key] = value
      this.$emit('input', rows)
    },
    validationError() {
      if (!this.enabled || this.definition.editable === false) return ''
      const value = this.value,
        definition = this.definition
      if (value === this.clearValue) return this.canClear ? '' : definition.property_name + '不允许清空属性内容'
      if (this.isMeasure) {
        if (!this.rows.length) return '请填写' + definition.property_name
        for (const row of this.rows) {
          const modules = this.modules(row)
          if (!modules.length) return '度量衡模板缺失'
          for (let index = 0; index < modules.length; index++) {
            const module = modules[index],
              input = row.measure_info.values[index]
            let rule = module.validate_rule || {}
            if (!input || String(input.value).trim() === '') return '请完整填写' + definition.property_name
            const unitRule = (rule.unit_rules || []).find((item) => String(item.unit_id) === String(input.unit_id))
            if (unitRule) rule = { ...rule, ...unitRule }
            if (module.input_type !== 'enum_diy' && ['int', 'integer', 'float', 'number'].includes(rule.data_type)) {
              const error = this.numericError(String(input.value), rule)
              if (error) return definition.property_name + '：' + error
            }
          }
        }
        return ''
      }
      if (Array.isArray(value) ? !value.length : value === null || value === undefined || String(value).trim() === '')
        return '请填写或选择' + definition.property_name
      if (
        definition.type === 'multi_select' &&
        Number(definition.multi_select_max) > 0 &&
        value.length > Number(definition.multi_select_max)
      )
        return definition.property_name + '超过允许的最多选择数'
      const rule = definition.validate_rule || {}
      if (definition.type === 'number' || ['int', 'integer', 'float', 'number'].includes(rule.data_type))
        return this.numericError(String(value), rule)
      return ''
    },
    numericError(value, rule) {
      if (!/^-?\d+(?:\.\d+)?$/.test(value) || !Number.isFinite(Number(value))) return '请输入有效数字'
      const unlimited =
        rule.min !== undefined && rule.max !== undefined && Number(rule.min) === 0 && Number(rule.max) === 0
      if (
        !unlimited &&
        rule.min !== undefined &&
        rule.min !== null &&
        rule.min !== '' &&
        Number(value) < Number(rule.min)
      )
        return '不能小于' + rule.min
      if (
        !unlimited &&
        rule.max !== undefined &&
        rule.max !== null &&
        rule.max !== '' &&
        Number(value) > Number(rule.max)
      )
        return '不能大于' + rule.max
      if (['int', 'integer'].includes(rule.data_type) && value.includes('.')) return '请输入整数'
      if (rule.precision !== undefined && (value.split('.')[1] || '').length > Number(rule.precision))
        return '小数位超出限制'
      return ''
    },
    focus() {
      const input = this.$el.querySelector('input')
      if (input) input.focus()
    }
  }
}
</script>

<style scoped>
.attribute-batch-input {
  width: 100%;
  min-width: 0;
}
.attribute-batch-input ::v-deep .el-input__inner {
  height: 28px;
  line-height: 28px;
  font-family: inherit;
  font-size: 11px;
}
.attribute-batch-input ::v-deep .el-select .el-input__inner {
  height: 28px !important;
  line-height: 28px !important;
}
.attribute-batch-input .el-button {
  font-family: inherit;
  font-size: 11px;
}
.attribute-batch-input > .el-select {
  width: 100%;
}
.measure-row > .el-select {
  width: 100%;
}
.measure-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.measure-module {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.measure-module .el-input,
.measure-module .el-select {
  width: 130px;
  max-width: 100%;
}
</style>
